<template>
  <div class="card">
    <div class="card-header bg-primary d-flex justify-content-between">
      <h5 class="mb-0 text-white">High Interest Findings</h5>
    </div>
    <div class="card-body">
      <ErrorSummary :error="error" />

      <!-- Explanation Text -->
      <div class="alert alert-info mb-4" role="alert">
        <div class="mb-2">
          The information below is organized to identify patterns and areas of interest to help develop investigative action plans. It is recommended to review
          relevant reports prior to initiating follow-up action.
        </div>
        <div>
          Please note this dashboard represents highlights of the represented categories where questionnaire consistency scores were higher than 70% and should
          not be interpreted as a comprehensive assessment of all employee disclosures.
        </div>
      </div>

      <div v-if="isLoading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading interview breakdowns...</p>
      </div>
      <div v-else-if="hasBuckets" class="container-fluid px-0">
        <div class="row">
          <div class="col-lg-4 mb-3" v-for="bucket in bucketList" :key="bucket.key">
            <InterviewBucketCard
              :title="bucket.title"
              :tooltip="bucket.tooltip"
              :interviews="bucket.interviews"
              :totalCount="bucket.totalCount"
              :organizationId="investigation.organizationId"
              @star-updated="onStarUpdated"
            />
          </div>
        </div>
      </div>
      <div v-else class="alert alert-light text-center" role="alert">
        No interview breakdowns data available
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorSummary from "@/components/ErrorSummary.vue";
import InterviewBucketCard from "./InterviewBucketCard.vue";

export default {
  components: {
    ErrorSummary,
    InterviewBucketCard,
  },
  props: {
    investigation: Object,
    isLoading: Boolean,
  },
  data() {
    return {
      error: null,
    };
  },
  computed: {
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator", "isInvestigationCreator", "isInvestigationViewer"]),
    hasBuckets() {
      return this.investigation && this.investigation.interviewBuckets;
    },
    bucketList() {
      if (!this.hasBuckets) return [];

      const buckets = this.investigation.interviewBuckets;
      return [
        {
          key: "highConsistencyWithContactAndObservations",
          title: "Contact Requests by Observation Count",
          tooltip:
            "Employees displayed in this category requested to be contacted, achieved consistency scores over 70%, and are listed in order of most to least observations provided.",
          interviews: buckets.highConsistencyWithContactAndObservations?.interviews || [],
          totalCount: buckets.highConsistencyWithContactAndObservations?.interviews?.length || 0,
        },
        {
          key: "highConsistencyWithContactAndAdmissions",
          title: "Contact Requests by Significant Admissions",
          tooltip:
            "Employees displayed in this category requested to be contacted, achieved consistency scores over 70%, and are listed in order of most to least admissions provided.",
          interviews: buckets.highConsistencyWithContactAndAdmissions?.interviews || [],
          totalCount: buckets.highConsistencyWithContactAndAdmissions?.interviews?.length || 0,
        },
        {
          key: "highConsistencyNoAdmissionsNoContact",
          title: "Observations by Count",
          tooltip:
            'Employees displayed in this category reported Observations, achieved consistency scores over 70%, and the top 10 are listed in order of most to least Observations provided. Employees listed did NOT select the "Contact Me" option on the questionnaire and did NOT report significant admissions.',
          interviews: buckets.highConsistencyNoAdmissionsNoContact?.interviews || [],
          totalCount: buckets.highConsistencyNoAdmissionsNoContact?.interviews?.length || 0,
        },
        {
          key: "highConsistencyWithSignificantAdmissions",
          title: "Significant Admissions",
          tooltip:
            "Employees displayed in this category reported Significant Admissions, achieved consistency scores over 70%, and the top 10 are listed in order of most to least Admissions provided.",
          interviews: buckets.highConsistencyWithSignificantAdmissions?.interviews || [],
          totalCount: buckets.highConsistencyWithSignificantAdmissions?.interviews?.length || 0,
        },
        {
          key: "highConsistencyWithTechnicalIssues",
          title: "Skips and/or Timeouts by Count",
          tooltip:
            "Employees displayed in this category intentionally Skipped questions or allowed questions to Timeout, achieved consistency scores over 70%, and the top 10 are listed in order of most to least Skips/Timeouts.",
          interviews: buckets.highConsistencyWithTechnicalIssues?.interviews || [],
          totalCount: buckets.highConsistencyWithTechnicalIssues?.interviews?.length || 0,
        },
        {
          key: "topNonPreferredAnswers",
          title: '"Attitude" Disclosures by Count',
          tooltip:
            "Employees displayed in this category reported Forgiving Attitudes towards counterproductive/criminal behavior, achieved consistency scores over 70%, and the top 10 are listed in order of most to least disclosures provided.",
          interviews: buckets.topNonPreferredAnswers?.interviews || [],
          totalCount: buckets.topNonPreferredAnswers?.interviews?.length || 0,
        },
        {
          key: "willingnessToReport",
          title: '"Willingness to Report" Disclosures by Count',
          tooltip:
            "Employees displayed in this category reported Unwillingness to Report colleagues/coworkers involved in counterproductive/criminal behavior, achieved consistency scores over 70%, and the top 10 are listed in order of most to least disclosures provided.",
          interviews: buckets.willingnessToReport?.interviews || [],
          totalCount: buckets.willingnessToReport?.interviews?.length || 0,
        },
      ];
    },
  },
  methods: {
    onStarUpdated(updatedInterview) {
      // Emit the star update event to the parent component
      this.$emit("star-updated", updatedInterview);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";
</style>
